import { Activity, Loader2, Building2, UserCircle, Mail } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Button } from '@/components/ui/Button.js';
import { Input } from '@/components/ui/Input.js';
import { Label } from '@/components/ui/Label.js';
import { Card, CardContent } from '@/components/ui/Card.js';
import { api, ApiError, type InvitationDescription } from '@/api/client.js';
import { useAuth } from '@/lib/auth.js';

/**
 * /invite/:token
 *
 * UX states once the invitation is described:
 *   - Signed in → "Attach to your account" button. If the invitation email isn't
 *     already on the user, accepting adds it.
 *   - Signed out → split flow: pick a name + password to create a new user OR
 *     sign in first to attach.
 *   - The describe response carries `emailKnown`: if true and the visitor is
 *     signed out we steer them to sign in rather than offer new-user mode (the
 *     server would refuse new-mode anyway because the email is already taken).
 */
export function InvitePage(): JSX.Element {
  const { token } = useParams({ strict: false }) as { token?: string };
  const navigate = useNavigate();
  const { status, user, refresh } = useAuth();

  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'invalid'; message: string }
    | { kind: 'ready'; description: InvitationDescription }
  >({ kind: 'loading' });

  useEffect(() => {
    if (!token) {
      setState({ kind: 'invalid', message: 'No invitation token in the URL.' });
      return;
    }
    api.invitations
      .describe(token)
      .then((description) => setState({ kind: 'ready', description }))
      .catch((err) => {
        const message =
          err instanceof ApiError
            ? err.status === 404
              ? 'This invitation is not valid. It may have been already accepted, revoked, or expired.'
              : err.message
            : 'Could not load this invitation.';
        setState({ kind: 'invalid', message });
      });
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md">
        <CardContent className="space-y-6 py-8">
          <Header />

          {state.kind === 'loading' && (
            <Centered>
              <Loader2 className="h-5 w-5 animate-spin" />
            </Centered>
          )}

          {state.kind === 'invalid' && (
            <div className="space-y-3 text-center">
              <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {state.message}
              </p>
              <Button variant="link" onClick={() => navigate({ to: '/login' })}>
                Go to sign-in
              </Button>
            </div>
          )}

          {state.kind === 'ready' && (
            <>
              <InvitationSummary description={state.description} />

              {status === 'loading' ? (
                <Centered>
                  <Loader2 className="h-5 w-5 animate-spin" />
                </Centered>
              ) : status === 'authenticated' && user ? (
                <AttachToCurrentUser
                  token={token!}
                  description={state.description}
                  currentUserName={user.name}
                  currentUserEmail={user.email}
                  onDone={async () => {
                    await refresh();
                    await navigate({
                      to: '/orgs/$slug',
                      params: { slug: state.description.organisation.slug },
                    });
                  }}
                />
              ) : (
                <SignedOutBranch
                  token={token!}
                  description={state.description}
                  onDone={async () => {
                    await refresh();
                    await navigate({ to: '/' });
                  }}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Header(): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
        <Activity className="h-5 w-5" strokeWidth={2.5} />
      </div>
      <h1 className="text-xl font-semibold">You've been invited to DeployIt</h1>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="flex justify-center py-6 text-muted-foreground">{children}</div>;
}

function InvitationSummary({ description }: { description: InvitationDescription }): JSX.Element {
  return (
    <div className="space-y-2 rounded-md border border-border bg-background/60 p-3 text-sm">
      <div className="flex items-center gap-2">
        <Building2 className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">{description.organisation.name}</span>
        <span className="text-xs text-muted-foreground">/{description.organisation.slug}</span>
      </div>
      <div className="text-xs text-muted-foreground">
        Invited by <span className="text-foreground">{description.invitedBy.name}</span> (
        {description.invitedBy.email})
      </div>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Mail className="h-3 w-3" />
        <span className="font-mono">{description.email}</span>
      </div>
    </div>
  );
}

function AttachToCurrentUser({
  token,
  description,
  currentUserName,
  currentUserEmail,
  onDone,
}: {
  token: string;
  description: InvitationDescription;
  currentUserName: string;
  currentUserEmail: string;
  onDone: () => void | Promise<void>;
}): JSX.Element {
  const { logout } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sameEmail = description.email.toLowerCase() === currentUserEmail.toLowerCase();

  async function accept() {
    setSubmitting(true);
    setError(null);
    try {
      await api.invitations.accept(token, { mode: 'attach' });
      await onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not accept the invitation.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-border bg-card/50 p-3 text-sm">
        <div className="flex items-center gap-2 font-medium">
          <UserCircle className="h-4 w-4 text-muted-foreground" />
          Signed in as {currentUserName}
        </div>
        <div className="ml-6 mt-0.5 font-mono text-xs text-muted-foreground">{currentUserEmail}</div>
        <p className="mt-2 text-xs text-muted-foreground">
          {sameEmail ? (
            <>
              You'll join <strong className="text-foreground">{description.organisation.name}</strong>{' '}
              using this account.
            </>
          ) : (
            <>
              Accepting will add{' '}
              <strong className="font-mono text-foreground">{description.email}</strong> to your
              profile and use it as the contact email for{' '}
              <strong className="text-foreground">{description.organisation.name}</strong>.
            </>
          )}
        </p>
      </div>

      {error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      <Button className="w-full" onClick={accept} disabled={submitting}>
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Accept invitation'}
      </Button>

      <button
        type="button"
        className="block w-full text-center text-xs text-muted-foreground hover:text-foreground"
        onClick={() => {
          void logout();
        }}
      >
        Wrong account? Sign out and accept with a different one
      </button>
    </div>
  );
}

function SignedOutBranch({
  token,
  description,
  onDone,
}: {
  token: string;
  description: InvitationDescription;
  onDone: () => void | Promise<void>;
}): JSX.Element {
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.invitations.accept(token, { mode: 'new', name, password });
      await onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not accept the invitation.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-5">
      {description.emailKnown && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs">
          <p className="font-medium">This email is already attached to a DeployIt account.</p>
          <p className="mt-1 text-muted-foreground">
            <Link to="/login" className="text-primary underline-offset-4 hover:underline">
              Sign in
            </Link>{' '}
            with that account, then re-open this invitation link to attach{' '}
            {description.organisation.name}.
          </p>
        </div>
      )}

      <form className="space-y-3" onSubmit={onSubmit}>
        <div className="space-y-1.5">
          <Label htmlFor="name">Your name</Label>
          <Input
            id="name"
            autoFocus
            required
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={description.emailKnown}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Choose a password</Label>
          <Input
            id="password"
            type="password"
            required
            minLength={12}
            maxLength={72}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={description.emailKnown}
          />
          <p className="text-xs text-muted-foreground">At least 12 characters.</p>
        </div>
        {error && (
          <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}
        <Button type="submit" className="w-full" disabled={submitting || description.emailKnown}>
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create account & accept'}
        </Button>
      </form>

      <div className="text-center text-xs text-muted-foreground">
        Already have a DeployIt account?{' '}
        <Link to="/login" className="text-primary underline-offset-4 hover:underline">
          Sign in
        </Link>{' '}
        and re-open the invitation link.
      </div>
    </div>
  );
}
